import React from 'react';
import { AlertTriangle, Info } from 'lucide-react';
import {
    INTERNO_TARIFA_MAX,
    INTERNO_TARIFAS_SELECIONAVEIS,
    INTERNO_BANDEIRAS,
    getComissaoInternoTabela,
    isDentroPoliticaInterno,
} from '../lib/comissaoInterna';
import { FIDELIDADE_OPCOES, getFatorFidelidade, carenciaOpcoesCom, isCarenciaSolicitada } from '../lib/politicaComercial';

interface PlanoComercialInternoFieldsProps {
    /** Percentuais em escala de exibição (ex: 22, 50). */
    descTarifa: number;
    descBandeira: number;
    onChangeDescTarifa: (v: number) => void;
    onChangeDescBandeira: (v: number) => void;
    fidelidade?: string;
    onChangeFidelidade?: (v: string) => void;
    carencia?: string;
    onChangeCarencia?: (v: string) => void;
    showFidelidade?: boolean;
    showCarencia?: boolean;
}

const fmtPct = (v: number) => `${v.toFixed(1).replace('.', ',')}%`;
const fmtMult = (v: number) => `${v.toFixed(2).replace('.', ',')}x`;

/**
 * Formulário de plano do COMERCIAL INTERNO — desconto direto de tarifa (20% a
 * 30%, de 1 em 1) e bandeira (0/50/100%), com a comissão da tabela aparecendo
 * na hora. Substitui o pacote de "planos × público" do vendedor externo pra
 * essa modalidade (ver isVendedorInterno no chamador): sem Público A/B/C, sem
 * plano_codigo — o desconto escolhido AQUI já é a condição, não um índice pra
 * procurar num pacote fechado.
 *
 * Mostra só o percentual BASE (tabela). O multiplicador de Volume/Fidelidade/
 * Desconto e o bônus de prospecção ativa só existem no fechamento mensal
 * (ver src/lib/comissaoInternaV1.ts) — Volume em especial depende do total
 * vendido no mês inteiro, não dá pra prever negociação por negociação aqui.
 * Fidelidade aparece como informação (ela é um dos 3 pilares), mas não é
 * multiplicada nesta tela pra não sugerir uma precisão que a tela não tem.
 */
const PlanoComercialInternoFields: React.FC<PlanoComercialInternoFieldsProps> = ({
    descTarifa,
    descBandeira,
    onChangeDescTarifa,
    onChangeDescBandeira,
    fidelidade,
    onChangeFidelidade,
    carencia,
    onChangeCarencia,
    showFidelidade = true,
    showCarencia = true,
}) => {
    const renderFidelidade = showFidelidade && !!onChangeFidelidade;
    const renderCarencia = showCarencia && !!onChangeCarencia;

    const foraDaPolitica = !isDentroPoliticaInterno(descTarifa, descBandeira);
    const percentualBase = getComissaoInternoTabela(descTarifa, descBandeira);
    const fatorFidelidade = getFatorFidelidade(fidelidade, undefined);

    return (
        <div className="space-y-4">
            <div>
                <label className="block text-xs font-bold text-gray-700 dark:text-slate-300 uppercase mb-2">
                    Desconto na tarifa
                </label>
                <div className="flex flex-wrap gap-1.5 mb-2">
                    {INTERNO_TARIFAS_SELECIONAVEIS.map(t => {
                        const selected = !foraDaPolitica && descTarifa === t;
                        return (
                            <button
                                key={t}
                                type="button"
                                onClick={() => onChangeDescTarifa(t)}
                                className={`px-3 py-1.5 rounded-lg border text-sm font-mono font-semibold transition-all ${
                                    selected
                                        ? 'border-primary bg-primary text-white'
                                        : 'border-gray-200 dark:border-slate-700 text-gray-600 dark:text-slate-300 hover:border-primary/40 bg-white dark:bg-slate-900'
                                }`}
                            >
                                {t}%
                            </button>
                        );
                    })}
                </div>
                <button
                    type="button"
                    onClick={() => { if (!foraDaPolitica) onChangeDescTarifa(INTERNO_TARIFA_MAX + 1); }}
                    className={`w-full text-left rounded-xl border border-dashed px-4 py-2.5 transition-all ${
                        foraDaPolitica
                            ? 'border-orange-400 dark:border-orange-700 bg-orange-50 dark:bg-orange-900/20 ring-2 ring-orange-200 dark:ring-orange-900/40'
                            : 'border-gray-300 dark:border-slate-600 hover:border-orange-400 bg-white dark:bg-slate-900'
                    }`}
                >
                    <p className={`text-sm font-semibold ${foraDaPolitica ? 'text-orange-600 dark:text-orange-400' : 'text-gray-600 dark:text-slate-300'}`}>
                        Fora da política — acima de 30%
                    </p>
                    <p className="text-[11px] text-orange-500/80">Exige aprovação do gerente</p>
                </button>

                {foraDaPolitica && (
                    <div className="mt-2">
                        <label className="block text-[11px] font-bold text-gray-700 dark:text-slate-300 uppercase mb-1">
                            Desconto na tarifa (%) — condição manual
                        </label>
                        <div className="relative max-w-[160px]">
                            <input
                                type="number"
                                required
                                min="0"
                                max="100"
                                value={descTarifa}
                                onChange={e => onChangeDescTarifa(Number(e.target.value))}
                                className="w-full border border-orange-200 dark:border-orange-900/50 bg-white dark:bg-slate-900 text-gray-800 dark:text-slate-100 rounded-xl px-3 py-2 text-sm focus:border-orange-400 focus:ring-2 focus:ring-orange-200 outline-none transition-all"
                            />
                            <span className="absolute right-3 top-2 text-gray-400 text-sm">%</span>
                        </div>
                    </div>
                )}
            </div>

            <div>
                <label className="block text-xs font-bold text-gray-700 dark:text-slate-300 uppercase mb-2">
                    Desconto na bandeira
                </label>
                <div className="flex gap-1.5">
                    {INTERNO_BANDEIRAS.map(b => {
                        const selected = descBandeira === b;
                        return (
                            <button
                                key={b}
                                type="button"
                                onClick={() => onChangeDescBandeira(b)}
                                className={`flex-1 px-3 py-2 rounded-lg border text-sm font-mono font-semibold transition-all ${
                                    selected
                                        ? 'border-primary bg-primary text-white'
                                        : 'border-gray-200 dark:border-slate-700 text-gray-600 dark:text-slate-300 hover:border-primary/40 bg-white dark:bg-slate-900'
                                }`}
                            >
                                {b}%
                            </button>
                        );
                    })}
                </div>
            </div>

            <div className="rounded-xl border border-gray-200 dark:border-slate-700 bg-gray-50 dark:bg-slate-800/60 px-4 py-3">
                <p className="text-[11px] font-bold uppercase text-gray-500 dark:text-slate-400">Comissão desta negociação (base)</p>
                {foraDaPolitica ? (
                    <p className="text-sm text-orange-600 dark:text-orange-400 font-semibold mt-1">
                        Percentual fica sujeito à aprovação do gerente.
                    </p>
                ) : (
                    <>
                        <p className="text-2xl font-bold text-primary mt-0.5">{fmtPct(percentualBase)}</p>
                        <p className="flex items-start gap-1.5 text-[11px] text-gray-500 dark:text-slate-400 mt-1.5">
                            <Info size={12} className="shrink-0 mt-0.5" />
                            <span>
                                Percentual base sobre {descTarifa}% tarifa · {descBandeira}% bandeira. O valor final também
                                depende do volume batido no mês e de bônus (prospecção ativa, SLA) — apurado no fechamento mensal.
                            </span>
                        </p>
                    </>
                )}
            </div>

            {renderFidelidade && (
                <div>
                    <label className="block text-xs font-bold text-gray-700 dark:text-slate-300 uppercase mb-2">
                        Fidelidade <span className="font-normal normal-case text-gray-400 dark:text-slate-500">(um dos 3 pilares do multiplicador mensal)</span>
                    </label>
                    <div className="grid grid-cols-2 gap-2">
                        {FIDELIDADE_OPCOES.map(o => {
                            const selected = fidelidade === o.valor;
                            const fator = getFatorFidelidade(o.valor, undefined);
                            return (
                                <button
                                    key={o.valor}
                                    type="button"
                                    onClick={() => onChangeFidelidade!(o.valor)}
                                    className={`rounded-xl border px-3 py-2 text-left transition-all flex items-center justify-between gap-2 ${
                                        selected
                                            ? 'border-primary bg-primary/5 ring-2 ring-primary/20'
                                            : 'border-gray-200 dark:border-slate-700 hover:border-primary/40 bg-white dark:bg-slate-900'
                                    }`}
                                >
                                    <span className="text-xs font-semibold text-gray-700 dark:text-slate-200">{o.label}</span>
                                    <span className="text-[11px] font-mono font-bold text-gray-500 dark:text-slate-400">{fmtMult(fator)}</span>
                                </button>
                            );
                        })}
                    </div>
                </div>
            )}

            {renderCarencia && (
                <div>
                    <label className="block text-xs font-bold text-gray-700 dark:text-slate-300 uppercase mb-2">
                        Carência <span className="font-normal normal-case text-gray-400 dark:text-slate-500">(para solicitações)</span>
                    </label>
                    <div className="grid grid-cols-2 gap-2">
                        {carenciaOpcoesCom(carencia).map(o => {
                            const selected = carencia === o.valor || (o.valor === 'sem carência' && !isCarenciaSolicitada(carencia));
                            return (
                                <button
                                    key={o.valor}
                                    type="button"
                                    onClick={() => onChangeCarencia!(o.valor)}
                                    className={`rounded-xl border px-3 py-2 text-left transition-all ${
                                        selected
                                            ? 'border-primary bg-primary/5 ring-2 ring-primary/20'
                                            : 'border-gray-200 dark:border-slate-700 hover:border-primary/40 bg-white dark:bg-slate-900'
                                    }`}
                                >
                                    <span className="text-xs font-semibold text-gray-700 dark:text-slate-200">{o.label}</span>
                                </button>
                            );
                        })}
                    </div>
                    {isCarenciaSolicitada(carencia) && (
                        <p className="flex items-center gap-1.5 text-[11px] text-orange-600 dark:text-orange-400 mt-2">
                            <AlertTriangle size={12} className="shrink-0" />
                            Carência exige aprovação do gerente ou admin — a solicitação será enviada ao salvar.
                        </p>
                    )}
                </div>
            )}
        </div>
    );
};

export default PlanoComercialInternoFields;
